import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { FileText, Search, Tag } from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import BlogCard from "../components/BlogCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ["All", "Guides", "Tutorials", "Games", "Strategy", "Safety"];

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${API}/blogs`);
        setBlogs(response.data);
        setFilteredBlogs(response.data);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  useEffect(() => {
    let result = blogs;

    if (searchQuery) {
      result = result.filter(blog =>
        blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory !== "All") {
      result = result.filter(blog => blog.category === selectedCategory);
    }

    setFilteredBlogs(result);
  }, [searchQuery, selectedCategory, blogs]);

  return (
    <>
      <Helmet>
        <title>Casino Blog – Guides, Tips & Strategies 2026 – PlaySpiro</title>
        <meta name="description" content="Read expert casino guides, slot tips, crash game strategies and bonus hunting advice. Stay ahead with PlaySpiro's regularly updated gambling blog." />
        <link rel="canonical" href="https://playspiro.com/blog" />
        <meta property="og:title" content="Casino Blog – Guides, Tips & Strategies 2026 – PlaySpiro" />
        <meta property="og:description" content="Expert casino guides, slot tips and crash game strategies at PlaySpiro." />
        <meta property="og:url" content="https://playspiro.com/blog" />
        <meta property="og:type" content="website" />
      </Helmet>
    <div className="min-h-screen py-8" data-testid="blog-page">
      {/* Header */}
      <section className="gradient-hero py-12 mb-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 font-['Montserrat']">
            Casino <span className="text-primary">Blog</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Tips, guides, and the latest news from the world of online casinos. 
            Stay informed and play smarter.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {/* Search & Categories */}
        <div className="glass rounded-2xl p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 w-full md:w-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 w-full"
                data-testid="blog-search-input"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat
                      ? "bg-primary text-white"
                      : "bg-white/5 text-white/60 hover:text-white hover:bg-white/10"
                  }`}
                  data-testid={`category-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Featured Article */}
        {filteredBlogs.length > 0 && !searchQuery && selectedCategory === "All" && (
          <div className="glass rounded-2xl overflow-hidden mb-8">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative h-64 md:h-auto">
                <img
                  src={filteredBlogs[0].featured_image}
                  alt={filteredBlogs[0].title}
                  className="w-full h-full object-cover"
                 loading="lazy"/>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card md:bg-gradient-to-t" />
              </div>
              <div className="p-6 md:p-8 flex flex-col justify-center">
                <Badge className="w-fit mb-4 bg-primary/20 text-primary">Featured</Badge>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 font-['Montserrat']">
                  {filteredBlogs[0].title}
                </h2>
                <p className="text-white/60 mb-6">{filteredBlogs[0].excerpt}</p>
                <a
                  href={`/blog/${filteredBlogs[0].slug}`}
                  className="inline-flex items-center text-primary font-medium hover:underline"
                >
                  Read Article →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Inline Ad */}
        <div className="ad-banner h-[90px] rounded-xl mb-8" data-testid="ad-blog-inline">
          <span>Ad Space 728x90</span>
        </div>

        {/* Blog Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-80 rounded-2xl bg-card animate-pulse" />
            ))}
          </div>
        ) : filteredBlogs.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBlogs.slice(searchQuery || selectedCategory !== "All" ? 0 : 1).map((blog) => (
              <BlogCard key={blog.id} blog={blog} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 text-lg">No articles found.</p>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
